import type { SortingState } from "@tanstack/react-table";
import { Users } from "lucide-react";
import { useCallback, useState } from "react";
import { PERMISSIONS } from "@/auth/permissions";
import { DeleteUserModal } from "@/components/admin/DeleteUserModal";
import { EditUserModal } from "@/components/admin/EditUserModal";
import { InviteUserModal } from "@/components/admin/InviteUserModal";
import { UsersPagination } from "@/components/admin/UsersPagination";
import { UsersTable } from "@/components/admin/UsersTable";
import { UsersToolbar } from "@/components/admin/UsersToolbar";
import { Card, EmptyState, Spinner } from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import { useChangeUserStatus, useRoles, useUsers } from "@/hooks/useAdmin";
import { useUser } from "@/hooks/useAuth";
import { useHasPermission } from "@/hooks/usePermission";
import type { ManagedUser, UserStatus, UsersQueryParams } from "@/types/rbac";

export function UsersPage() {
  const currentUser = useUser();
  const { data: roles = [] } = useRoles();
  const changeStatus = useChangeUserStatus();
  const canCreate = useHasPermission(PERMISSIONS.USERS.CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.USERS.UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.USERS.DELETE);

  const [params, setParams] = useState<UsersQueryParams>({
    page: 1,
    pageSize: 10,
    search: "",
    status: "all",
    roleId: "all",
    sortBy: "name",
    sortOrder: "asc",
  });

  const { data, isLoading, isFetching } = useUsers(params);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<ManagedUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<ManagedUser | null>(null);

  const handleSearchChange = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  function handleStatusChange(status: UserStatus | "all") {
    setParams((prev) => ({ ...prev, status, page: 1 }));
  }

  function handleRoleChange(roleId: string | "all") {
    setParams((prev) => ({ ...prev, roleId, page: 1 }));
  }

  function handleSortingChange(newSorting: SortingState) {
    setSorting(newSorting);
    if (newSorting.length > 0) {
      const col = newSorting[0]!;
      setParams((prev) => ({
        ...prev,
        sortBy: col.id as UsersQueryParams["sortBy"],
        sortOrder: col.desc ? "desc" : "asc",
        page: 1,
      }));
    }
  }

  function handlePageChange(page: number) {
    setParams((prev) => ({ ...prev, page }));
  }

  function handlePageSizeChange(pageSize: number) {
    setParams((prev) => ({ ...prev, pageSize, page: 1 }));
  }

  function handleToggleStatus(user: ManagedUser) {
    const newStatus = user.status === "active" ? "disabled" : "active";
    changeStatus.mutate({
      userId: user.id,
      payload: { status: newStatus },
    });
  }

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <PageHeader
          title="Agenci"
          description="Konta agentów, upoważnienia w towarzystwach i egzaminy nowych osób"
        />
        <div className="flex justify-center py-24">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      <PageHeader
        title="Agenci"
        description="Konta agentów, upoważnienia w towarzystwach i egzaminy nowych osób"
      />

      <Card padding="sm">
        <UsersToolbar
          search={params.search ?? ""}
          onSearchChange={handleSearchChange}
          status={params.status ?? "all"}
          onStatusChange={handleStatusChange}
          roleId={params.roleId ?? "all"}
          onRoleChange={handleRoleChange}
          roles={roles}
          onInvite={() => {
            setInviteOpen(true);
          }}
          canInvite={canCreate}
        />
      </Card>

      <Card padding="none">
        {data && data.items.length > 0 ? (
          <>
            <UsersTable
              data={data.items}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              onEdit={setEditUser}
              onToggleStatus={handleToggleStatus}
              onDelete={setDeleteUser}
              currentUserId={currentUser?.id ?? ""}
              isLoading={isFetching && !isLoading}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
            <div className="px-4">
              <UsersPagination
                page={data.page}
                pageSize={data.pageSize}
                totalCount={data.totalCount}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        ) : (
          <EmptyState
            icon={<Users size={32} />}
            title="Brak agentów"
            description="Nie znaleziono agentów pasujących do wybranych filtrów."
          />
        )}
      </Card>

      <InviteUserModal
        isOpen={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
        }}
      />
      <EditUserModal
        user={editUser}
        onClose={() => {
          setEditUser(null);
        }}
      />
      <DeleteUserModal
        user={deleteUser}
        onClose={() => {
          setDeleteUser(null);
        }}
      />
    </div>
  );
}
